import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { setUser } from '../../actions/userActions';
import { setNetworks } from '../../actions/networksActions';
import { setupUserSession } from '../../services/auth';
import { useVerifyMfaMutation } from '../../services/user';
import { getItem, storeItem, removeItem } from '../../helpers/store';
import { trackEvent } from '../../helpers/analytics';
import { isMfaInvalidCodeError } from '../../components/molecules/MfaCodeInput/mfaSubmitErrors';
import {
  MFA_CHALLENGE_TOKEN_KEY,
  MFA_REDIRECT_KEY,
  MFA_INVITE_KEY,
  clearMfaChallengeStorage,
} from '../../hooks/useSignIn';
import { useSocialAuth } from '../../hooks/useSocialAuth';

// Accepts a post-verify redirect only when it points back at this app. Anything that
// parses to a different origin (or fails to parse) is dropped, so the backend-supplied
// `redirect` can never turn into an open redirect to an attacker-controlled host.
const safeRedirectPath = (redirect) => {
  if (!redirect) return null;
  try {
    const url = new URL(redirect, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (e) {
    return null;
  }
};

export const useMfaChallenge = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [verifyMfa, { isLoading }] = useVerifyMfaMutation();

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryToken = query.get('challenge_token');
  const queryRedirectRaw = query.get('redirect');
  const stateInvite = location.state?.isInvite;
  const stateFrom = location.state?.from;

  // Persist whatever arrived (via the URL query or router state) BEFORE stripping the
  // sensitive params from the URL, so the token / destination / invite-flag survive both that
  // cleanup and a full page reload. Then strip them so they don't linger in history,
  // bookmarks, or the Referer header.
  useEffect(() => {
    if (queryToken) {
      // A fresh challenge from an email deep link does NOT pass through useSignIn's
      // clearMfaChallengeStorage, so reset the companions before persisting this link's own
      // values — otherwise an abandoned challenge's destination/invite (e.g. on a shared
      // browser) would bleed into this login.
      storeItem(MFA_CHALLENGE_TOKEN_KEY, queryToken);
      removeItem(MFA_REDIRECT_KEY);
      removeItem(MFA_INVITE_KEY);
    }
    // Post-verify destination arrives either as an emailed same-origin `redirect` or an in-app
    // protected-route `from` (router state); persist whichever so a reload keeps it.
    const fromPath = stateFrom?.pathname ? `${stateFrom.pathname}${stateFrom.search || ''}` : null;
    const safeRedirect = safeRedirectPath(queryRedirectRaw) || safeRedirectPath(fromPath);
    if (safeRedirect) storeItem(MFA_REDIRECT_KEY, safeRedirect);
    if (stateInvite !== undefined) storeItem(MFA_INVITE_KEY, stateInvite ? '1' : '0');

    if (!queryToken && !queryRedirectRaw) return;

    const cleaned = new URLSearchParams(location.search);
    cleaned.delete('challenge_token');
    cleaned.delete('redirect');
    const search = cleaned.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }, [
    queryToken,
    queryRedirectRaw,
    stateInvite,
    stateFrom?.pathname,
    stateFrom?.search,
    history,
    location.pathname,
    location.search,
  ]);

  // Read with a storage fallback so a deep link whose params were just stripped — or a full
  // reload of /mfa/verify — still resolves the token, destination, and invite flag.
  //   token:    1. router state (in-app login)  2. URL query (email deep link)  3. storage
  //   redirect: query / stored same-origin destination (in-app uses `from` below)
  //   isInvite: router state, else stored flag — must survive reload, or the deferred invite
  //             email-match in useSocialAuth silently skips and a mismatched OAuth account
  //             could complete sign-in.
  const challengeToken = location.state?.challengeToken || queryToken || getItem(MFA_CHALLENGE_TOKEN_KEY);
  const queryRedirect = safeRedirectPath(queryRedirectRaw) || safeRedirectPath(getItem(MFA_REDIRECT_KEY));
  const from = location.state?.from;
  const isInvite = location.state?.isInvite ?? getItem(MFA_INVITE_KEY) === '1';

  useEffect(() => {
    if (!challengeToken) return;

    trackEvent('authentication:otp:shown');
  }, [challengeToken]);

  const handleSocialAuth = useSocialAuth(isInvite);

  const verifyCode = (code, { useBackupCode = false } = {}) =>
    verifyMfa({ challenge_token: challengeToken, code: code.trim() })
      .unwrap()
      .then((response) => {
        handleSocialAuth(response.user);

        trackEvent('authentication:otp:success', { method: useBackupCode ? 'backup_code' : 'otp' });
        const user = setupUserSession(response.user);

        clearMfaChallengeStorage();
        dispatch(setUser(user));
        dispatch(setNetworks(user));

        if (from?.pathname) {
          history.replace({ pathname: from.pathname, search: from.search });
        } else if (queryRedirect) {
          history.replace(queryRedirect);
        } else {
          history.replace('/');
        }

        return user;
      })
      .catch((error) => {
        if (!error?.isInviteMismatch && isMfaInvalidCodeError(error)) {
          trackEvent('authentication:otp:invalid_code', { method: useBackupCode ? 'backup_code' : 'otp' });
        }

        if (error?.isInviteMismatch) {
          // Verify succeeded but the OAuth account doesn't match the invite, so the backend
          // has consumed the challenge. Drop the half-established auth token AND the now-spent
          // challenge token/destination so a reload doesn't loop on a dead challenge; the
          // component surfaces the mismatch reason inline via mapMfaSubmitError.
          removeItem('authToken');
          clearMfaChallengeStorage();
        }

        throw error;
      });

  return {
    challengeToken,
    verifyCode,
    isLoading,
  };
};
